<template>
  <v-text-field
    :model-value="displayValue"
    :label="label"
    variant="outlined"
    :density="density"
    :disabled="disabled"
    hide-details="auto"
    @input="onPhoneInput"
    :error="!phoneNumberMetadata.isValid && !!phoneValue"
  >
    <!-- :error-messages="!phoneNumberMetadata.isValid && !!phoneValue ? ['Invalid phone number'] : []" -->
    <template v-slot:prepend-inner>
      <v-menu>
        <template v-slot:activator="{ props }">
          <div v-bind="props" class="country-code-trigger d-flex align-center">
            <span>+{{ selectedCountryDialCode }}</span>
            <v-icon size="small" class="ml-1">mdi-menu-down</v-icon>
          </div>
        </template>
        <v-list>
          <v-list-item
            v-for="(country, code) in filteredCountries"
            :key="code"
            @click="selectCountry(code)"
          >
            <v-list-item-title>
              {{ country.name }} (+{{ country.phone[0] }})
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>
  </v-text-field>
</template>

<script>
import { parsePhoneNumber } from 'awesome-phonenumber';
import phoneCountries from 'src/assets/data/phone-countries.json';

export default {
  name: 'VuetifyPhoneInput',
  props: {
    initial: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: 'Phone Number',
    },
    density: {
      type: String,
      default: 'compact',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    preferredCountries: {
      type: Array,
      default: () => ['US'],
    },
    ignoredCountries: {
      type: Array,
      default: () => [],
    },
    onlyCountries: {
      type: Array,
      default: null,
    },
    useBrowserLocale: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      selectedCountry: 'US',
      phoneValue: '',
      inputValue: '',
      phoneCountries,
      phoneNumberMetadata: {
        isValid: false,
        formattedNumber: '',
        countryCode: null,
      },
    };
  },
  computed: {
    selectedCountryDialCode() {
      return this.phoneCountries[this.selectedCountry].phone[0];
    },
    displayValue() {
      return this.inputValue;
    },
    filteredCountries() {
      let countries = { ...this.phoneCountries };

      // Apply only countries filter
      if (this.onlyCountries) {
        countries = Object.fromEntries(
          Object.entries(countries).filter(([code]) =>
            this.onlyCountries.includes(code)
          )
        );
      }

      // Remove ignored countries
      if (this.ignoredCountries) {
        countries = Object.fromEntries(
          Object.entries(countries).filter(
            ([code]) => !this.ignoredCountries.includes(code)
          )
        );
      }

      // Move preferred countries to the top
      const preferredList = this.preferredCountries
        .filter((code) => countries[code])
        .map((code) => [code, countries[code]]);

      const otherCountries = Object.entries(countries)
        .filter(([code]) => !this.preferredCountries.includes(code))
        .sort((a, b) => a[1].name.localeCompare(b[1].name));

      return Object.fromEntries([...preferredList, ...otherCountries]);
    },
  },
  mounted() {
    this.initializePhone();
  },
  watch: {
    initial(n) {
      if (n) {
        this.initializePhone();
      }
    },
  },
  methods: {
    initializePhone() {
      // Try to detect country from browser locale
      if (this.useBrowserLocale && !this.initial) {
        const browserLocale = this.getBrowserLocale();
        if (browserLocale) {
          this.selectedCountry = browserLocale;
        }
      }

      if (!this.initial) {
        return;
      }

      try {
        const phoneNumber = parsePhoneNumber(this.initial, {
          regionCode: this.selectedCountry,
        });

        if (phoneNumber.valid) {
          this.selectedCountry = phoneNumber.regionCode || this.selectedCountry;
          this.phoneValue = phoneNumber.number?.national?.replace(/\D/g, '');
          this.inputValue = phoneNumber?.number?.national;
          this.updatePhoneNumberMetadata(phoneNumber);
        } else {
          // If not valid, try parsing with different country codes
          for (const [code, country] of Object.entries(this.phoneCountries)) {
            const tryPhoneNumber = parsePhoneNumber(this.initial, {
              regionCode: code,
            });
            if (tryPhoneNumber.valid) {
              this.selectedCountry = tryPhoneNumber.regionCode || code;
              this.phoneValue = tryPhoneNumber.number?.national?.replace(
                /\D/g,
                ''
              );
              this.inputValue = tryPhoneNumber.number?.national;
              this.updatePhoneNumberMetadata(tryPhoneNumber);
              break;
            }
          }

          // If still not valid, just set the input value
          if (!this.phoneNumberMetadata.isValid) {
            this.phoneValue = this.initial.replace(/\D/g, '');
            this.inputValue = this.initial;
          }
        }
      } catch (error) {
        console.error('Phone initialization error:', error);
        this.phoneValue = this.initial.replace(/\D/g, '');
        this.inputValue = this.initial;
      }
    },
    getBrowserLocale() {
      // Simple browser locale to country code mapping
      const locale = navigator.language || navigator.userLanguage;
      const countryCode = locale.split('-')[1]?.toUpperCase();
      return countryCode && this.phoneCountries[countryCode]
        ? countryCode
        : null;
    },
    selectCountry(countryCode) {
      this.selectedCountry = countryCode;
      this.emitPhoneNumber();
    },
    onPhoneInput(event) {
      const input = event.target.value;
      this.inputValue = input;

      // Remove non-digit and plus sign characters
      const cleaned = input.replace(/[^\d+]/g, '');

      // Check for international prefix
      if (cleaned.startsWith('+')) {
        // Try to detect country from the prefix
        for (const [code, country] of Object.entries(this.phoneCountries)) {
          const dialCode = country.phone[0].toString();
          if (cleaned.startsWith(`+${dialCode}`)) {
            this.selectedCountry = code;
            this.phoneValue = cleaned.slice(dialCode.length + 1);
            break;
          }
        }
      } else {
        this.phoneValue = cleaned;
      }

      this.emitPhoneNumber();
    },
    emitPhoneNumber() {
      if (!this.inputValue.trim() && !this.phoneValue) {
        this.$emit('update:phone', null);
        this.$emit('change', null);
        return;
      }

      try {
        const phoneNumber = parsePhoneNumber(this.phoneValue, {
          regionCode: this.selectedCountry,
        });
        // Attempt to detect country from number if possible
        const detectedRegion = phoneNumber.regionCode;
        if (detectedRegion && detectedRegion !== this.selectedCountry) {
          this.selectedCountry = detectedRegion;
        }

        this.updatePhoneNumberMetadata(phoneNumber);

        // We're only going to emit if it's valid.  So when folks are typing on dispositions it doesn't patch errors to the backend
        if (phoneNumber.valid) {
          const e164Number = phoneNumber.number?.e164;
          this.inputValue = phoneNumber.number?.national;
          this.phoneValue = this.inputValue.replace(/\D/g, '');

          this.$emit('update:phone', e164Number);
          this.$emit('change', e164Number);
        }
      } catch (error) {
        console.error('Phone number parsing error:', error);
      }
    },
    updatePhoneNumberMetadata(phoneNumber) {
      this.phoneNumberMetadata = {
        isValid: phoneNumber?.valid,
        formattedNumber: phoneNumber.number?.national,
        countryCode: phoneNumber?.regionCode,
      };
    },
  },
};
</script>

<style scoped>
.country-code-trigger {
  cursor: pointer;
  margin-right: 8px;
  user-select: none;
}

:deep(.v-field__prepend-inner) {
  padding-right: 0;
  align-items: center;
}
</style>
